/**
 * The table's children — one set, two projections.
 *
 * ============================== WHY THIS FILE EXISTS ============================
 * Phase 7 adds a second view of the same table: the saloon, where the plate is tilted
 * and the cast stands round it (`SaloonTable.tsx`). The rule for that phase is ONE
 * TABLE, TWO PROJECTIONS — the saloon may not have a seat plate, a timer, a bet pile,
 * a board, a pot, a deck or a hand of its own. So every piece `TopDownTable` drew
 * inline moved here, VERBATIM, and both views compose them. What differs between the
 * two is where each piece is put, and that is each view's business alone.
 *
 * The move changed no markup: every element, class, `data-td` anchor, label and
 * attribute is what `TopDownTable` emitted before, in the same order, and the
 * 480-combination sweep and the felt measurements are the proof (Phase 7 report §1).
 * The flight layer and the bubble placer find things by their `data-td` anchors, so a
 * piece that renders here renders an anchor they can already measure, in either view.
 *
 * Two pieces gained a parameter, and they are the only changes to a child:
 *
 *   - `TimerRing` has a `variant`. `"ring"` is the SVG ring round an avatar, exactly as
 *     before; `"bar"` is decision D9's brass under-bar on a saloon plate. One component,
 *     one `--t`, one `hurry` class, one transition — the clock that drives it cannot
 *     tell which it is drawing.
 *   - `useBubbles` remembers which bubble is NEWEST, so the live region can announce
 *     that one alone (Phase 6 review, answer 5 — box 0(5) of this phase).
 * ===============================================================================
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { TableState } from "@engine/table-state";
import { PlayingCard } from "../components/PlayingCard";
import { ChipStacks } from "./ChipStacks";
import { SHAPE, type Durations } from "./pace";
import type { DeckState } from "./useScene";
import type { Payoff } from "./payoff";
import { avatarUrlForName, avatarUrl as avatarUrlOf, type Expression } from "../art/roster";
import { cardLabel } from "../lib/table-game";
import { useMoney } from "../lib/MoneyContext";
import { BucksIcon } from "../lib/mark";
import { Counted, type Bubble } from "./flight";
import type { TauntItem } from "./lines";

/**
 * How far round a seat's timer ring has gone, and whether it is in the hurry-up.
 *
 * Phase 3 added `from` and `ms`: the ring transitions from `from` to `t` over `ms`,
 * in CSS, so a bot's three seconds of thinking is one prop write and no re-renders.
 * Without them the ring simply sits at `t`, which is what the harness fixtures do.
 */
export interface SeatTimer { t: number; hurry?: boolean; from?: number; ms?: number }

/** The motion custom properties both views put on `.td-app`. */
export function motionVars(motion: Durations | undefined): React.CSSProperties | undefined {
  return motion ? {
    "--flip-ms": `${motion.flip}ms`, "--hop-ms": `${motion.buttonHop}ms`,
    "--fade-ms": `${motion.actionBeat}ms`,
    /* Phase 3b: every duration the stylesheet animates with, from `Durations`. */
    "--td-ms-flip": `${motion.flip}ms`, "--td-ms-hop": `${motion.buttonHop}ms`,
    "--td-ms-settle": `${motion.settle}ms`, "--td-ms-winpulse": `${motion.winPulse}ms`,
    "--td-ms-shuffle": `${motion.shuffle}ms`, "--td-ms-fade": `${motion.actionBeat}ms`,
  } as React.CSSProperties : undefined;
}

/**
 * A seat's stack, and the ALL IN tag that stands in front of it.
 *
 * ===================== PHASE 2B, BOX 0(a): ONE ANCHOR ==========================
 * The number is ALWAYS rendered and always carries `data-td="stacknum"`, because that
 * is the anchor a pot flies to. It used to be replaced by the ALL IN tag, so a seat
 * that was all in had no number for the chips to land on and the pot arrived on the
 * seat's NAME.
 *
 * While the seat is all in the number is taken out of flow and hidden — the tag keeps
 * exactly the width and position it had before, so nothing on the plate moves — and it
 * is held at ZERO. That is what makes the payoff read: when the push lands, the
 * reducer clears `allIn` (see `display.ts`), the number returns to flow, and `Counted`
 * counts it up from 0 to the new stack over the same beat the chips take to arrive.
 * ===============================================================================
 */
export function StackNum({ allIn, stack, ms }: { allIn: boolean; stack: number; ms?: number }) {
  return (
    <span className="num td-stack" data-td="stacknum" data-allin={allIn ? "1" : undefined}>
      <span className="td-stack-n"><Counted value={allIn ? 0 : stack} ms={ms} /></span>
      {allIn ? <span className="td-tag-state">ALL IN</span> : null}
    </span>
  );
}

/**
 * One portrait, wherever a face goes.
 *
 * A silhouette when the roster has no face for this name, rather than an empty ring —
 * which is what the hero got on the live route, because the engine names the viewer's
 * own seat "You" and "You" is not in the cast. An empty gold circle beside a name reads
 * as a failed image; a silhouette reads as "no portrait yet", which is the truth until
 * Phase 2b lets a player choose one.
 *
 * The geometry is identical either way, so dropping real portraits in later is a
 * content change and not a layout change — the same property `GameTable`'s own
 * `Portrait` was written to hold.
 */
export function Portrait({ name, expression = "neutral", className, override, slug }: {
  name: string;
  expression?: Expression;
  className: string;
  /** Phase 2b: the hero's chosen face, which is not resolved from a cast name. */
  override?: string | null;
  /**
   * Phase 6, box 0(3): this SITTING'S face for this seat.
   *
   * Given when `art/casting.ts` has moved a seat off the roster's own slug — because
   * the hero's persona was wearing it, or because two names share one (`agnew` and
   * `bosley` both wear `dealer-m`). The name is untouched; only the picture moves.
   */
  slug?: string;
}) {
  const src = override ?? (slug ? avatarUrlOf(slug, expression) : avatarUrlForName(name, expression));
  if (src) return <img className={className} src={src} alt="" aria-hidden="true" loading="lazy" />;
  return (
    <svg className={`${className} td-av-blank`} viewBox="0 0 40 40"
         role="img" aria-label={`${name}, no portrait`}>
      <circle cx="20" cy="15" r="7.2" />
      <path d="M5.5 40c0-8.2 6.3-13.2 14.5-13.2S34.5 31.8 34.5 40z" />
    </svg>
  );
}

/**
 * The dealer button's hop (Phase 3b).
 *
 * The disc is placed by the seat's percentages, so when the button moves the element
 * jumps to its new pixel on render. This measures where it was, where it is, and plays
 * the difference backwards with a lift at the midpoint (`hopLift` of the disc's height)
 * over `--td-ms-hop` — the FLIP technique, on the independent `translate` property so
 * the disc's own centring `transform` is untouched. No motion without `motion`.
 */
export function useButtonHop(ref: React.RefObject<HTMLElement | null>, button: number, hopMs: number | undefined) {
  const last = useRef<{ button: number; x: number; y: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const prev = last.current;
    last.current = { button, x: r.left, y: r.top };
    if (!prev || prev.button === button || !hopMs) return;
    const dx = prev.x - r.left, dy = prev.y - r.top;
    if (dx === 0 && dy === 0) return;
    const lift = r.height * SHAPE.hopLift;
    el.animate([
      { translate: `${dx}px ${dy}px`, offset: 0 },
      { translate: `${dx / 2}px ${dy / 2 - lift}px`, offset: 0.5 },
      { translate: "0px 0px", offset: 1 },
    ], { duration: hopMs, easing: "ease-in-out" });
  }, [ref, button, hopMs]);
}

/**
 * The timer, wherever a seat's clock is drawn.
 *
 * One component because the hero has one too — and the hero is the only player at the
 * table who has a real clock: bots get 2–4 seconds of thinking time, the hero gets ten
 * quiet seconds and then a five-second hurry-up that acts for them on expiry. A ring on
 * every opponent and none on the person whose clock actually runs out would have been
 * exactly backwards.
 *
 * ## Two variants, one clock (decision D9)
 *
 * `"ring"` — the top-down table's: an SVG ring round the avatar. `pathLength="100"`
 * makes the dash array a percentage regardless of the avatar's pixel size, so the same
 * two numbers work at 38px and at 64px.
 *
 * `"bar"` — the saloon's: a brass bar under the seat's plate, its fill `--t` of the
 * plate's width. Same `--t`, same `from`/`ms` transition written the same way, same
 * `hurry` state (`td-bar-hurry` beside `td-ring-hurry`, styled oxblood and pulsing in
 * `saloon.css` exactly as the ring is in `topdown.css`). No timing lives here; the
 * Director writes `--t`.
 */
export function TimerRing({ timer, variant = "ring" }: { timer: SeatTimer | undefined; variant?: "ring" | "bar" }) {
  /*
   * The transition, done by hand so it always starts where it is told to.
   *
   * Two writes: `--t` to `from` with no transition, a forced style read, then `--t` to
   * `t` with the transition on. Written as one style object React would paint only
   * the destination and the ring would appear already run down. `--t` is a registered
   * `<number>` property in `topdown.css`, which is what lets it transition at all.
   */
  const ref = useRef<SVGSVGElement & HTMLSpanElement>(null);
  const from = timer?.from, t = timer?.t ?? 0, ms = timer?.ms ?? 0;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (from !== undefined && ms > 0) {
      el.style.setProperty("--t-ms", "0ms");
      el.style.setProperty("--t", String(from));
      void el.getBoundingClientRect();
      el.style.setProperty("--t-ms", `${ms}ms`);
      el.style.setProperty("--t", String(t));
    } else {
      el.style.setProperty("--t-ms", "0ms");
      el.style.setProperty("--t", String(t));
    }
  }, [from, t, ms, variant]);
  if (!timer) return null;
  if (variant === "bar") {
    return (
      <span ref={ref} className={"td-bar" + (timer.hurry ? " td-bar-hurry" : "")}
            data-td="timer" aria-hidden="true"
            style={{ "--t": timer.t } as React.CSSProperties}>
        {/* The track is the bar itself; the fill is `--t` of it. */}
        <span className="td-bar-fill" />
      </span>
    );
  }
  return (
    <svg ref={ref} className={"td-ring" + (timer.hurry ? " td-ring-hurry" : "")}
         viewBox="0 0 100 100" aria-hidden="true" focusable="false"
         style={{ "--t": timer.t } as React.CSSProperties}>
      {/* The track first: a ring that is two-thirds empty has to read as a ring
          running down, not as a stray gold scratch. */}
      <circle className="td-ring-track" cx="50" cy="50" r="45" />
      <circle className="td-ring-arc" cx="50" cy="50" r="45" pathLength={100} />
    </svg>
  );
}

/* ------------------------------------------------------------ per seat --- */

type Seat = TableState["seats"][number];

/** Is this seat's chair empty? A busted opponent's is; the player's own never is. */
export function seatIsEmpty(s: Seat): boolean {
  return s.sittingOut || (s.stack <= 0 && s.folded);
}

/** The state classes a seat carries in either view. */
export function seatClass(s: Seat, i: number, state: TableState, payoff: Payoff | null,
                          shown: Record<number, readonly number[]> | undefined): string {
  return "td-seat" + (s.isSelf ? " td-seat-hero" : "") +
         (state.toAct === i ? " td-seat-acting" : "") +
         (s.folded ? " td-seat-folded" : "") +
         (payoff?.winners.includes(i) ? " td-seat-winner" : "") +
         (payoff?.losers.includes(i) ? " td-seat-loser" : "") +
         /* Cards shown: the taunt bubble has to clear them. */
         (!s.folded && (s.hole?.length === 2 || shown?.[i]?.length === 2)
           ? " td-seat-shown" : "");
}

/**
 * A seat's two cards on the felt: the face-down peek, or a hand turned over.
 *
 * Face down while the hand is live — the peek is all a seat gets, per the spec's "two
 * face-down cards peeking behind the avatar". Face UP the moment `hole` is non-null,
 * which happens exactly when the engine decides this viewer is entitled to see them:
 * at a showdown, and never before. The renderer does not decide that; it only draws
 * what the snapshot already allows.
 *
 * For the HERO this draws only a hand they chose to SHOW (Phase 6): they have no
 * face-down peek — their hand is the two big cards — and both cases Show exists for
 * (an uncontested win, a fold) are cases nothing else draws. Returns null otherwise,
 * exactly as the two inline blocks this replaced did.
 */
export function SeatPeek({ s, i, shown, dealt, className, style }: {
  s: Seat; i: number;
  shown: Record<number, readonly number[]> | undefined;
  dealt: Record<number, number> | undefined;
  /** Saloon: its placement class. Top-down: none. */
  className?: string;
  style?: React.CSSProperties;
}) {
  const cls = (base: string) => base + (className ? ` ${className}` : "");
  if (s.isSelf) {
    return shown?.[i]?.length === 2 ? (
      <span className={cls("td-peek td-peek-shown td-flip")} data-td="peek" style={style}>
        {shown[i]!.map((c, k) => <PlayingCard key={k} card={c} kind="seat" />)}
      </span>
    ) : null;
  }
  if (s.folded) return null;
  /*
   * Phase 6: `shown` is a seat that CHOSE to turn its cards over.
   *
   * Drawn exactly like a showdown — the same `td-peek-shown td-flip`, so it gets the
   * flip Phase 3b built and no second animation exists for the same picture. The state
   * still says `hole: null`, because the engine never decided the viewer was entitled
   * to them; the seat decided, which is a different thing and is why it arrives beside
   * the state instead of inside it.
   */
  return (s.hole && s.hole.length === 2) || shown?.[i]?.length === 2 ? (
    <span className={cls("td-peek td-peek-shown td-flip")} data-td="peek" style={style}>
      {(s.hole && s.hole.length === 2 ? s.hole : shown![i]!)
        .map((c, k) => <PlayingCard key={k} card={c} kind="seat" />)}
    </span>
  ) : (
    /* Phase 3: with `dealt`, backs appear as the cards land. The span is always there
       so the deal has somewhere to fly to. */
    <span className={cls("td-peek")} data-td="peek" aria-hidden="true" style={style}>
      {Array.from({ length: dealt ? Math.min(2, dealt[i] ?? 0) : 2 }, (_, k) => (
        <PlayingCard key={k} kind="seat" hidden back={s.equipped.cardBack} />
      ))}
    </span>
  );
}

/**
 * The plate: name and stack.
 *
 * The name is on the plate itself as well as in it, because on a small felt the visible
 * line is hidden and the portrait carries the identity — a screen reader should still
 * get the name.
 *
 * The STACK is in the label too, and it was not. An `aria-label` replaces everything
 * inside the element it is on, so a plate labelled with a name alone read out the name
 * and swallowed the number — which is the thing on a seat a player is actually reading,
 * and from Phase 5 the biggest thing on the plate. `say()` is the money context's own
 * spoken form, so it says "Bucks" or "chips" the way the rest of the felt does.
 *
 * `children` is the saloon's: the plate carries its portrait disc and its brass timer
 * bar. The top-down plate has none, and renders exactly what it always did.
 */
export function SeatTag({ s, motion, className, style, children }: {
  s: Seat; motion?: Durations; className?: string; style?: React.CSSProperties;
  children?: React.ReactNode;
}) {
  const { say } = useMoney();
  return (
    <span className={"td-tag" + (className ? ` ${className}` : "")} style={style}
          aria-label={`${s.identity.name}, ${s.allIn ? "all in" : say(s.stack)}`}>
      {children}
      <b>{s.identity.shortName}</b>
      <StackNum allIn={s.allIn} stack={s.stack} ms={motion?.betSlide} />
    </span>
  );
}

/**
 * The bet: the chips in front of a seat, and the number beside them.
 *
 * In top-down it rides on the SEAT, not on a ring of its own (Phase 3b): parented to
 * the seat it inherits the same anchor and the same push, so it is laid down one
 * card-length further in than the cards are. In the saloon it is placed at the
 * projected bet spot. Either way it is the same element with the same anchor, and an
 * invisible one exists while a bet can still arrive, so a chip has a measured place to
 * fly to before the bet exists.
 */
export function SeatBet({ s, i, left, flights, className, style }: {
  s: Seat; i: number;
  /** The label rides on the side toward the middle of the table. */
  left: boolean;
  flights: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  const { fmt, say } = useMoney();
  const extra = className ? ` ${className}` : "";
  if (s.committed > 0 && !s.folded) {
    return (
      <span className={"td-bet td-bet-seat" + (left ? " td-bet-left" : "") + extra}
            data-td="bet" data-seat={i} style={style}
            aria-label={`${s.identity.name} has ${say(s.committed)} in front`}>
        {/* Phase 3b: the bet as the chips it is made of; the label rides beside the
            pile, on the side toward the middle of the table. */}
        <ChipStacks key={s.committed} amount={s.committed} />
        <span className="num">{fmt(s.committed)}</span>
      </span>
    );
  }
  if (flights && !s.folded) {
    return (
      <span className={"td-bet td-bet-seat td-bet-empty" + extra} data-td="bet" data-seat={i}
            style={style} aria-hidden="true"><span className="td-chips" /></span>
    );
  }
  return null;
}

/* ------------------------------------------------------ the middle --- */

/** The disc, from `export/ui/`. Letters are drawn over it in code, never in the art. */
export const DISC = "/art/ui/disc-ivory.webp";

/** The dealer button. Placement is the view's; the hop is shared. */
export function DealerButton({ state, motion, style, className }: {
  state: TableState; motion?: Durations; style?: React.CSSProperties; className?: string;
}) {
  const discRef = useRef<HTMLSpanElement>(null);
  useButtonHop(discRef, state.button, motion?.buttonHop);
  return (
    <span ref={discRef} className={"td-dbtn" + (className ? ` ${className}` : "")} style={style} data-td="dbtn"
          role="img" aria-label={`${state.seats[state.button]?.identity.name ?? "seat"} is on the button`}>
      <img src={DISC} alt="" aria-hidden="true" />
      <span>D</span>
    </span>
  );
}

/** Phase 3b: the deck at the dealer's spot. Five backs while the hand is dealt
    (riffling during the shuffle), a two-card stub afterwards; the deal comes from it
    and the muck goes to it. */
export function DeckSpot({ deck, style, className }: { deck: DeckState; style?: React.CSSProperties; className?: string }) {
  if (deck === "none") return null;
  return (
    <span className={"td-deckspot" + (deck === "shuffling" ? " td-deck-shuffling" : "") + (className ? ` ${className}` : "")}
          data-td="deck" aria-hidden="true" style={style}>
      {Array.from({ length: deck === "stub" ? SHAPE.deckStub : SHAPE.deckCards }, (_, k) => (
        <span key={k} className={"td-deckcard " + (k % 2 ? "td-deck-b" : "td-deck-a")}
              style={{ "--k": k } as React.CSSProperties}>
          <PlayingCard kind="seat" hidden />
        </span>
      ))}
    </span>
  );
}

/** The five board slots. */
export function Board({ board, style, className }: { board: readonly number[]; style?: React.CSSProperties; className?: string }) {
  return (
    <div className={"td-board" + (className ? ` ${className}` : "")} data-td="board" style={style}>
      {board.map((c, i) => (
        /* Keyed by slot, so a card that is already out never flips again.
           Phase 3b: a real flip — the back turns away and the face turns in. */
        <span key={`b${i}`} className="td-flip3d">
          <span className="td-flip3d-back"><PlayingCard kind="board" hidden /></span>
          <span className="td-flip3d-face"><PlayingCard card={c} kind="board" /></span>
        </span>
      ))}
      {Array.from({ length: 5 - board.length }, (_, i) => (
        <span key={`slot${i}`} className="td-slot" aria-hidden="true" />
      ))}
    </div>
  );
}

/**
 * What has been COLLECTED into the middle: the pot less what is still in front of the
 * seats, which is drawn there.
 *
 * The pile lags the state while bets are in the air: `collect` lands the moment the
 * copies take off (so a chip is never in two places), and the pile would otherwise grow
 * before anything had arrived. While frozen it draws what it drew last; the ref is
 * refreshed only when not frozen.
 */
export function useCollected(state: TableState | null, potFrozen: boolean): number {
  const lastCollected = useRef(0);
  if (!state) return 0;
  const collectedNow = Math.max(0, state.pot - state.seats.reduce((t, x) => t + (x.folded ? 0 : x.committed), 0));
  const collected = potFrozen ? lastCollected.current : collectedNow;
  if (!potFrozen) lastCollected.current = collectedNow;
  return collected;
}

/** Phase 3b: the pot is the pill (the number) with the chips it holds laid under it. */
export function PotWrap({ pot, collected, potFlying, motion, style, className }: {
  pot: number; collected: number; potFlying: boolean; motion?: Durations;
  style?: React.CSSProperties; className?: string;
}) {
  return (
    <div className={"td-potwrap" + (pot <= 0 ? " td-pot-empty" : "") + (className ? ` ${className}` : "")} style={style}>
      <div className="td-pot" data-td="pot">
        Pot <span className="num"><Counted value={pot} ms={motion?.potCollect} /></span>
      </div>
      {/* The chips under the pill are what has been COLLECTED: the pot less what is
          still in front of the seats, which is drawn there. The pill's number is the
          whole pot, as Phase 2 had it. */}
      <span className={"td-potchips" + (potFlying ? " td-potchips-flying" : "")} data-td="potchips">
        <ChipStacks key={collected} amount={collected} />
      </span>
    </div>
  );
}

/** The hero's two big cards. Placement is the view's; what they show is shared. */
export function HeroHand({ hero, heroIndex, dealt, payoff }: {
  hero: Seat; heroIndex: number; dealt: Record<number, number> | undefined; payoff: Payoff | null;
}) {
  return (
    <div className={"td-hand" + (hero.hole && hero.hole.length === 2 ? " td-flip" : "")
                    + (payoff?.winners.includes(heroIndex) ? " td-hand-winner" : "")
                    + (payoff?.losers.includes(heroIndex) ? " td-hand-loser" : "")} data-td="hand">
      {/* Phase 3: a folded hero's cards have gone to the muck. Only when the Director
          is driving (`dealt`); the fixtures keep drawing the hand. */}
      {dealt && hero.folded
        ? null
        : hero.hole && hero.hole.length === 2
        ? hero.hole.map((c, i) => (
            <PlayingCard key={`h${i}`} card={c} kind="hero" />
          ))
        : Array.from({ length: dealt ? Math.min(2, dealt[heroIndex] ?? 0) : 2 }, (_, i) => (
            <PlayingCard key={`hb${i}`} kind="hero" hidden back={hero.equipped.cardBack} />
          ))}
    </div>
  );
}

/* ------------------------------------------------------------ bubbles --- */

/**
 * The bubbles to draw, in seat order, with the hero's flagged — and which is newest.
 *
 * A string in `taunts` is a line — the shape the Phase 2 harness has always passed.
 * An id that nothing knows draws nothing, rather than an empty cream rectangle.
 *
 * PHASE 7 BOX 0(5): the NEWEST bubble, for the live region. At nine seats with three
 * bubbles up the region used to read all three in one breath; it now reads the one
 * that just appeared. "Newest" is the bubble whose seat-and-line was not up on the
 * previous render — worked out here, from what is on screen, rather than asked of the
 * voice, so the harness's fixtures and the live table get the same answer.
 */
export function useBubbles(state: TableState | null, taunts: Record<number, string | TauntItem> | undefined,
                           heroIndex: number): { bubbles: Bubble[]; newest: Bubble | null } {
  const prev = useRef<{ keys: Set<string>; newest: string | null }>({ keys: new Set(), newest: null });
  const bubbles: Bubble[] = [];
  if (state) {
    for (const [key, value] of Object.entries(taunts ?? {})) {
      const seat = Number(key);
      if (!Number.isInteger(seat) || !state.seats[seat]) continue;
      const item = typeof value === "string"
        ? { id: `raw_${seat}`, kind: "line" as const, label: value, text: value }
        : value;
      if (!item || !item.text) continue;
      bubbles.push({ seat, item, hero: seat === heroIndex });
    }
  }
  const keyOf = (b: Bubble) => `${b.seat}:${b.item.id}:${b.item.text}`;
  const keys = new Set(bubbles.map(keyOf));
  const arrived = bubbles.filter((b) => !prev.current.keys.has(keyOf(b)));
  let newestKey = prev.current.newest;
  if (arrived.length) newestKey = keyOf(arrived[arrived.length - 1]!);
  if (newestKey && !keys.has(newestKey)) newestKey = bubbles.length ? keyOf(bubbles[bubbles.length - 1]!) : null;
  prev.current = { keys, newest: newestKey };
  const newest = bubbles.find((b) => keyOf(b) === newestKey) ?? null;
  return { bubbles, newest };
}

/**
 * The table, spoken: the felt is a picture, and this is the same information — the
 * board and the pot — for a screen reader.
 */
export function BoardStatus({ state }: { state: TableState }) {
  const { say } = useMoney();
  return (
    <p className="sr-only" role="status">
      {state.board.length
        ? `Board: ${state.board.map(cardLabel).join(", ")}.`
        : "No community cards yet."}{" "}
      Pot {say(state.pot)}.
    </p>
  );
}

/**
 * And the words, once, for a screen reader.
 *
 * The bubble itself is inside an `aria-hidden` layer — everything in there is a
 * picture of something that is also somewhere else. A taunt's TEXT is not anywhere
 * else, so it is announced here, politely, and a sticker or an emoji is announced by its
 * label rather than by its character. Phase 7 box 0(5): the NEWEST one only.
 */
export function TalkStatus({ state, newest }: { state: TableState; newest: Bubble | null }) {
  return (
    <p className="sr-only" role="status" aria-live="polite">
      {newest ? `${state.seats[newest.seat]?.identity.shortName ?? "A player"}: ${newest.item.label}` : ""}
    </p>
  );
}

/* ------------------------------------------------------------ top bar --- */

/** What the top bar shows about the player's money. Phase 4 replaces the source. */
export interface WalletView {
  /** Buster Bucks on the account. */
  balance: number;
  /** What this sitting has done to it, signed. Omit for a session not yet started. */
  session?: number;
}

/**
 * The table's own bar — wordmark, context line, wallet, ⋯ — for a table drawn on its
 * own (`chrome="own"`: the harness). Inside a room the room's header is the only header
 * and this is not drawn. The saloon view lays it over the top of the scene, as m11
 * does; the markup is the same.
 */
export function TopBar({ state, context, lastAction, wallet, settings, onSettings, className, extra }: {
  /**
   * PHASE 8: only what the bar reads — the seat count, the hand number and a seat's short
   * name for the last action — so the blackjack table (which has no `TableState`) draws
   * the same bar. A `TableState` is one of these; nothing about poker's bar changed.
   */
  state: { seats: readonly { identity: { shortName: string } }[]; handNumber: number };
  context?: string;
  lastAction?: { seat: number; label: string } | null;
  wallet?: WalletView | null;
  settings?: ReactNode;
  onSettings?: () => void;
  className?: string;
  /** Phase 7: the saloon's Saloon / Top-down toggle sits in the bar, before the ⋯. */
  extra?: ReactNode;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const n = state.seats.length;
  return (
    <div className={"td-top" + (className ? ` ${className}` : "")}>
      <span className="td-wm">BluffBusters</span>
      <span className="td-ctx">
        {context ?? `${n}-max · hand ${state.handNumber}`}
        {lastAction ? ` · ${state.seats[lastAction.seat]?.identity.shortName ?? ""} ${lastAction.label}` : ""}
      </span>
      <span className="td-sp" />
      {wallet && (
        <span className="td-wallet" aria-label={`Wallet: ${wallet.balance.toLocaleString("en-US")} Bucks`}>
          <BucksIcon />
          <span className="td-wallet-bal num">{wallet.balance.toLocaleString("en-US")}</span>
          {wallet.session !== undefined && wallet.session !== 0 && (
            <span className={`td-wallet-ses num ${wallet.session > 0 ? "td-wallet-up" : "td-wallet-down"}`}>
              {wallet.session > 0 ? "+" : "−"}
              {Math.abs(wallet.session).toLocaleString("en-US")}
            </span>
          )}
        </span>
      )}
      {extra}
      <button className="td-more" type="button" aria-label="Table settings"
              aria-expanded={settings ? settingsOpen : undefined}
              onClick={onSettings ?? (settings ? () => setSettingsOpen((o) => !o) : undefined)}>
        {/* The pill is the span; the button is the 44px target. See `.td-more`. */}
        <span>⋯</span>
      </button>
      {settings && settingsOpen && (
        <div className="td-settings" role="dialog" aria-label="Table settings">{settings}</div>
      )}
    </div>
  );
}
